import { Route } from '@angular/router';
import { AccessDenied403Component } from '@lb/auth/pages/access-denied-403/access-denied-403.component';
import { PageNotFoundComponent } from '@lb/auth/pages/page-not-found/page-not-found.component';
import { AccountIssueComponent } from '@lb/auth/pages/account-issue/account-issue.component';
import { ConfiguredRouteGuard } from '@lb/auth/route-guards/configured-route.guard';
import { AccountVerificationGuard } from '@lb/auth/route-guards/account-verification.guard';
import { IAppSettings } from '@lb/config/interfaces/iapp-settings.interface';
import { CalendarComponent } from './pages/calendar/calendar.component';
import { EnrollmentsMainComponent } from './pages/enrollments/main/enrollments-main.component';
import { HomeComponent } from './pages/home/home.component';
import { LessonsComponent } from './pages/lessons/lessons.component';
import { OrientationComponent } from './pages/orientation/orientation.component';
import { LearnerProfileComponent } from './pages/profile/learner-profile.component';
import { EnrollLearnerComponent } from './pages/enroll-a-learner/enroll-a-learner.component';
import { ScheduleComponent } from './pages/enrollments/schedule/schedule.component';
import { ChatComponent } from './pages/chat/chat.component';
import { OnboardingComponent } from './pages/onboarding/onboarding.component';
import { inject } from '@angular/core';
import { USER_VIEW_TOKEN, ENROLLMENT_USER_ID_TOKEN } from '@lb/components/enrollment-detail/enrollment-detail.tokens';
import { LearnerUserStateService } from './shared/state/learner-users-state.service';
import { USER_DROPDOWN_CRUMB_KEY } from './shared/components/user-drop-down/user-drop-down.component';
import { LessonDetailComponent } from '@lb/components';

export function createAppRoutes(appConfig: IAppSettings): Route[] {
	const routes: Route[] = [
		{
			path: 'onboard',
			component: OnboardingComponent,
			title: 'Set Up Your Account | LanguageBird'
		},
		{
			path: 'account-issue',
			component: AccountIssueComponent,
			title: 'MyLanguageBird Learner: Account Issue'
		},
		{
			path: '403',
			title: 'MyLanguageBird Learner: Access Denied',
			component: AccessDenied403Component
		},
		{
			path: '',
			canActivateChild: [ConfiguredRouteGuard],
			canActivate: [AccountVerificationGuard],
			data: {
				routeGuardSettings: {
					entitlements: ['Learner:App:Access', 'Admin:App:Access', 'Guardian:App:Access', 'PartnerContact:App:Access', 'Guest:App:Access'],
					redirect: '/assets/pages/403.html',
					alternativeEntitlements: ['Instructor:App:Access'],
					alternativeRedirect: appConfig.instructorAppUrl
				}
			},
			children: [
				{
					path: '',
					component: HomeComponent,
					title: 'MyLanguageBird Learner: Home',
					data: {
						breadcrumb: [{ linkIcon: 'untitled-ui/general/home-line', routerLink: '/' }]
					}
				},
				{
					path: 'enrollments',
					title: 'MyLanguageBird Learner: Enrollments',
					children: [
						{
							path: '',
							component: EnrollmentsMainComponent,
							data: {
								breadcrumb: [{ linkText: 'Enrollments', routerLink: '/enrollments' }]
							}
						},
						{
							path: ':id/schedule',
							component: ScheduleComponent,
							data: {
								breadcrumb: [{ linkText: 'Enrollments', routerLink: '/enrollments' }, { linkText: 'Schedule' }]
							}
						},
						{
							path: ':id',
							loadComponent: () =>
								import('@lb/components/enrollment-detail/enrollment-detail-shell.component').then((m) => m.EnrollmentDetailShellComponent),
							providers: [
								{ provide: USER_VIEW_TOKEN, useValue: 'learner' as const },
								{ provide: ENROLLMENT_USER_ID_TOKEN, useFactory: () => inject(LearnerUserStateService).activeLearnerUserID }
							],
							children: [
								{
									path: '',
									loadComponent: () =>
										import('@lb/components/enrollment-detail/enrollment-overview/enrollment-overview.component').then(
											(m) => m.EnrollmentOverviewComponent
										),
									data: {
										breadcrumb: [
											{ linkText: 'Enrollments', routerLink: '/enrollments' },
											{ linkText: 'Enrollment Detail', routerLink: '/enrollments/:id' },
											{ linkText: 'Overview' }
										]
									}
								},
								{
									path: 'lessons',
									loadComponent: () =>
										import('@lb/components/enrollment-detail/enrollment-lessons/enrollment-lessons.component').then(
											(m) => m.EnrollmentLessonsComponent
										),
									data: {
										breadcrumb: [
											{ linkText: 'Enrollments', routerLink: '/enrollments' },
											{ linkText: 'Enrollment Detail', routerLink: '/enrollments/:id' },
											{ linkText: 'Lessons' }
										]
									}
								},
								{
									path: 'members',
									loadComponent: () =>
										import('@lb/components/enrollment-detail/enrollment-members/enrollment-members.component').then(
											(m) => m.EnrollmentMembersComponent
										),
									data: {
										breadcrumb: [
											{ linkText: 'Enrollments', routerLink: '/enrollments' },
											{ linkText: 'Enrollment Detail', routerLink: '/enrollments/:id' },
											{ linkText: 'Members' }
										]
									}
								},
								{
									path: 'lessons/:lessonId',
									loadComponent: () =>
										import('@lb/components/enrollment-detail/enrollment-lesson-wrapper/enrollment-lesson-wrapper.component').then(
											(m) => m.EnrollmentLessonWrapperComponent
										),
									data: {
										breadcrumb: [
											{ linkText: 'Enrollments', routerLink: '/enrollments' },
											{ linkText: 'Enrollment Detail', routerLink: '/enrollments/:id' },
											{ linkText: 'Lesson' }
										]
									}
								}
							]
						}
					]
				},
				{
					path: 'calendar',
					component: CalendarComponent,
					title: 'MyLanguageBird Learner: Calendar',
					data: {
						breadcrumb: [{ linkText: 'Calendar', routerLink: '/calendar' }]
					}
				},
				{
					// Component-less parent: LessonsComponent has no <router-outlet>, so the detail must be a
					// sibling child rendered in the app outlet, not a child of the list (which left the old
					// `lessons/:lessonId` route dead). The parent crumb flattens with the child's to read
					// "Lessons › Lesson Details".
					path: 'lessons',
					title: 'MyLanguageBird Learner: Lessons',
					data: {
						breadcrumb: [{ linkText: 'Lessons', routerLink: '/lessons' }]
					},
					children: [
						{
							path: '',
							component: LessonsComponent
						},
						{
							path: ':lessonId',
							component: LessonDetailComponent,
							title: 'MyLanguageBird Learner: Lesson Details',
							providers: [{ provide: USER_VIEW_TOKEN, useValue: 'learner' as const }],
							data: {
								breadcrumb: [{ linkText: 'Lesson Details' }]
							}
						}
					]
				},
				{
					// Grade Report for the active learner (a learner's own, or a guardian's selected learner).
					// Behind the environment feature toggle, like the instructor page. The shell's guard settings
					// are repeated — minus Guest, whom the API does not admit — because a child's
					// routeGuardSettings REPLACES the inherited one.
					//
					// Partner contacts are not on the list (Ben, 2026-09-23 — Partner-Reports-Plan.md §11): their
					// copy is Reports › Individual Learner Grade Report, without the Order Transcript notice. The list is
					// any-of, so someone who is BOTH a partner contact and a guardian or learner still gets in —
					// deliberately: guardians keep this page the way learners do. Anyone refused lands Home
					// (E2), a partner typing an old bookmark included; that is the app's "not your page" rule
					// (the partner routes below redirect the same way), not the 403 page.
					path: 'grade-report',
					loadComponent: () => import('./pages/grade-report/grade-report.component').then((m) => m.GradeReportComponent),
					title: 'MyLanguageBird Learner: Grade Report',
					canActivate: [ConfiguredRouteGuard],
					data: {
						routeGuardSettings: {
							entitlements: ['Learner:App:Access', 'Admin:App:Access', 'Guardian:App:Access'],
							redirect: '',
							alternativeEntitlements: ['Instructor:App:Access'],
							alternativeRedirect: appConfig.instructorAppUrl,
							featureToggle: 'enableTranscripts',
							featureToggleRedirect: '/'
						}
					}
				},
				{
					// Partner Reports (docs/transcript/Partner-Reports-Plan.md): a card landing page with the
					// All Learner Grade Report and the Individual Learner Grade Report beneath it. Partner contacts only, and behind the same
					// transcripts toggle as the Grade Report. The guard settings sit once on this componentless parent:
					// its children inherit them (Angular's default emptyOnly inheritance), and the shell's
					// canActivateChild runs for every descendant, so a deep link to the report is bounced
					// exactly as the landing page is. Crumbs flatten parent + child, as the lessons routes do;
					// the landing page's empty-path child inherits the parent's crumb rather than repeating it
					// (BreadcrumbService drops an inherited crumb). The two reports were renamed on 2026-10-05
					// (10/2 show and tell); their paths keep the original names so bookmarks still open.
					// The breadcrumb learner switcher is hidden across the section (Ben, 2026-09-22): the
					// reports are institution-wide, and picking a learner there would navigate Home.
					path: 'reports',
					title: 'MyLanguageBird Learner: Reports',
					data: {
						routeGuardSettings: {
							entitlements: ['PartnerContact:App:Access'],
							redirect: '',
							featureToggle: 'enableTranscripts',
							featureToggleRedirect: '/'
						},
						breadcrumb: [{ linkText: 'Reports', routerLink: '/reports' }],
						hideRecurringBreadcrumbs: [USER_DROPDOWN_CRUMB_KEY]
					},
					children: [
						{
							path: '',
							loadComponent: () => import('./pages/reports/report-list/report-list.component').then((m) => m.ReportListComponent)
						},
						{
							path: 'partner-grade-report',
							loadComponent: () =>
								import('./pages/reports/partner-grade-report/partner-grade-report.component').then((m) => m.PartnerGradeReportComponent),
							title: 'MyLanguageBird Learner: All Learner Grade Report',
							data: {
								breadcrumb: [{ linkText: 'All Learner Grade Report' }]
							}
						},
						{
							// The partner's copy of /grade-report (§11): one learner, picked on the page, not through the switcher.
							path: 'learner-grade-report',
							loadComponent: () =>
								import('./pages/reports/learner-grade-report/learner-grade-report.component').then((m) => m.PartnerLearnerGradeReportComponent),
							title: 'MyLanguageBird Learner: Individual Learner Grade Report',
							data: {
								breadcrumb: [{ linkText: 'Individual Learner Grade Report' }]
							}
						}
					]
				},
				{
					path: 'orientation',
					component: OrientationComponent,
					title: 'MyLanguageBird Learner: Orientation',
					data: {
						breadcrumb: [{ linkText: 'Orientation', routerLink: '/orientation' }]
					}
				},
				{
					path: 'enroll-a-learner',
					component: EnrollLearnerComponent,
					title: 'MyLanguageBird Learner: Enroll a Learner',
					data: {
						breadcrumb: [{ linkText: 'Enroll a Learner', routerLink: '/enroll-a-learner' }],
						routeGuardSettings: {
							entitlements: ['PartnerContact:App:Access'],
							redirect: ''
						}
					}
				},
				{
					path: 'profile',
					component: LearnerProfileComponent,
					title: 'MyLanguageBird Learner: Profile',
					data: {
						breadcrumb: [{ linkText: 'Profile', routerLink: '/profile' }]
					}
				}
			]
		},
		{
			path: '**',
			title: 'MyLanguageBird Learner: Page Not Found',
			component: PageNotFoundComponent
		}
	];

	// Add chat route if enabled in config
	if (appConfig.chat?.enabled) {
		const mainRoute = routes.find((r) => r.path === '');
		if (mainRoute?.children) {
			mainRoute.children.push({
				path: 'chat',
				component: ChatComponent,
				title: 'MyLanguageBird Learner: Chat',
				data: {
					breadcrumb: [{ linkText: 'Chat', routerLink: '/chat' }]
				}
			});
		}
	}

	return routes;
}
